<template>
  <div class="page-wrap">
    <div class="panel-card">

      <!-- Header -->
      <div class="panel-header">
        <div class="panel-header__icon"><i class="fa fa-building"></i></div>
        <div class="panel-header__text">
          <div class="panel-header__title">{{ isEdit ? 'Edit Company' : 'Add Company' }}</div>
          <div class="panel-header__sub">{{ isEdit ? 'Update company details and contact information' : 'Register a new company in the system' }}</div>
        </div>
      </div>

      <div class="form-body">

        <!-- Section: Company Information -->
        <div class="form-section">
          <div class="section-header">
            <i class="fa fa-info-circle section-header__icon"></i>
            <span>Company Information</span>
          </div>

          <div class="toggle-row">
            <div class="toggle-item">
              <div class="toggle-item__text">
                <div class="toggle-item__label">Use in Archive</div>
                <div class="toggle-item__sub">Include this company in archival workflows</div>
              </div>
              <label class="toggle-switch">
                <input type="checkbox" v-model="use_in_archive" />
                <span class="toggle-track"><span class="toggle-thumb"></span></span>
              </label>
            </div>
            <div class="toggle-item">
              <div class="toggle-item__text">
                <div class="toggle-item__label">Active</div>
                <div class="toggle-item__sub">Enable this company across the system</div>
              </div>
              <label class="toggle-switch">
                <input type="checkbox" v-model="is_active" />
                <span class="toggle-track"><span class="toggle-thumb"></span></span>
              </label>
            </div>
          </div>

          <div class="field-grid">
            <div class="field-group" :class="{ 'field-group--error': errors['company_name'] }">
              <label class="field-label">Company Name <span class="req">*</span></label>
              <input v-model="company_name" type="text" class="field-input" placeholder="Enter company name" />
              <span class="field-error" v-if="errors['company_name']">{{ errors['company_name'][0] }}</span>
            </div>
            <div class="field-group" :class="{ 'field-group--error': errors['address'] }">
              <label class="field-label">Address <span class="req">*</span></label>
              <input v-model="address" type="text" class="field-input" placeholder="Enter address" />
              <span class="field-error" v-if="errors['address']">{{ errors['address'][0] }}</span>
            </div>
          </div>
        </div>

        <!-- Section: Contact Information -->
        <div class="form-section">
          <div class="section-header">
            <i class="fa fa-user section-header__icon"></i>
            <span>Contact Information</span>
          </div>

          <div class="field-grid">
            <div class="field-group" :class="{ 'field-group--error': errors['contact_name'] }">
              <label class="field-label">Contact Name <span class="req">*</span></label>
              <input v-model="contact_name" type="text" class="field-input" placeholder="Enter contact name" />
              <span class="field-error" v-if="errors['contact_name']">{{ errors['contact_name'][0] }}</span>
            </div>
            <div class="field-group" :class="{ 'field-group--error': errors['contact_phone_number'] }">
              <label class="field-label">Phone Number <span class="req">*</span></label>
              <input v-model="contact_phone_no" type="text" class="field-input" placeholder="(###)-###-####" v-mask="'(###)-###-####'" />
              <span class="field-error" v-if="errors['contact_phone_number']">{{ errors['contact_phone_number'][0] }}</span>
            </div>
          </div>

          <div class="field-grid field-grid--single">
            <div class="field-group" :class="{ 'field-group--error': errors['contact_email'] }">
              <label class="field-label">Email Address <span class="req">*</span></label>
              <input v-model="contact_email" type="email" class="field-input" placeholder="Enter email address" />
              <span class="field-error" v-if="errors['contact_email']">{{ errors['contact_email'][0] }}</span>
            </div>
          </div>
        </div>

      </div>
    </div>

    <!-- Floating save bar -->
    <div class="save-bar">
      <button class="save-bar__back" @click="goBack()">
        <i class="fa fa-arrow-left"></i> Back
      </button>
      <div class="save-bar__right">
        <template v-if="!isEdit">
          <button class="save-bar__secondary" ref="kt_save_add_another_company"
            @click="store('SAA', 'kt_save_add_another_company')">
            <i class="fa fa-plus"></i> Save & Add Another
          </button>
          <button class="save-bar__primary" ref="kt_save_company"
            @click="store('S', 'kt_save_company')">
            <i class="fa fa-check"></i> Save
          </button>
        </template>
        <button v-else class="save-bar__primary" ref="kt_update_company_data"
          @click="update('kt_update_company_data')">
          <i class="fa fa-check"></i> Update
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { SET_BREADCRUMB } from "@/core/services/store/mutations.type";
import {
  STORE_MASTER_COMPANY,
  GET_MASTER_COMPANY_DATA,
  UPDATE_MASTER_COMPANY,
} from "@/core/services/store/actions.type";

export default {
  data() {
    return {
      isEdit: false,
      breadCrumbs: [
        { id: 1, title: "Dashboard", route: "client.dashboard" },
        { id: 2, title: "Companies", route: "client.master.company" },
        { id: 3, title: "Create New Company", route: "" },
      ],
      company_name: "",
      address: "",
      contact_name: "",
      contact_email: "",
      contact_phone_no: "",
      use_in_archive: false,
      is_active: true,
      errors: [],
    };
  },
  created() {
    this.$store.commit(SET_BREADCRUMB, this.breadCrumbs);
    this.isEdit = this.$route.name === "client.master.company.edit";
    if (this.isEdit) {
      this.breadCrumbs[2].title = "";
      this.$store.dispatch(GET_MASTER_COMPANY_DATA, this.$route.params.id)
        .then(data => {
          this.breadCrumbs[2].title = "Edit | " + data.company_name;
          this.company_name     = data.company_name;
          this.address          = data.address;
          this.contact_name     = data.contact_name;
          this.contact_email    = data.contact_email;
          this.contact_phone_no = data.contact_phone_number;
          this.is_active        = data.is_active;
          this.use_in_archive   = data.use_in_archive;
        })
        .catch(() => {
          this.$toastr.e("Company detail not found!");
          this.$router.push({ name: "client.master.company" });
        });
    }
  },
  methods: {
    goBack() {
      this.$router.push({ name: "client.master.company", params: { client_slug: this.$route.params.client_slug } });
    },
    clearForm() {
      this.company_name = "";
      this.address = "";
      this.contact_name = "";
      this.contact_email = "";
      this.contact_phone_no = "";
      this.use_in_archive = false;
      this.is_active = true;
    },
    loadingButton(ref) {
      const btn = this.$refs[ref];
      btn.disabled = true;
      btn.classList.add("spinner", "spinner-light", "spinner-right");
    },
    closeBtnLoad(ref) {
      const btn = this.$refs[ref];
      btn.classList.remove("spinner", "spinner-light", "spinner-right");
      btn.disabled = false;
    },
    payload() {
      return {
        company_name:         this.company_name,
        address:              this.address,
        contact_name:         this.contact_name,
        contact_email:        this.contact_email,
        contact_phone_number: this.contact_phone_no,
        is_active:            this.is_active,
        use_in_archive:       this.use_in_archive,
      };
    },
    update(ref) {
      this.errors = [];
      this.loadingButton(ref);
      this.$store.dispatch(UPDATE_MASTER_COMPANY, { id: this.$route.params.id, data: this.payload() })
        .then(data => {
          this.closeBtnLoad(ref);
          if (data.code === 200) {
            this.$toastr.s(data.msg);
            this.$router.push({ name: "client.master.company" });
          } else {
            this.$toastr.w(data.msg);
          }
        })
        .catch(err => { this.errors = err; this.closeBtnLoad(ref); });
    },
    store(saveType, ref) {
      this.errors = [];
      this.loadingButton(ref);
      this.$store.dispatch(STORE_MASTER_COMPANY, this.payload())
        .then(data => {
          this.$toastr.s(data.msg);
          this.closeBtnLoad(ref);
          if (saveType === "S") {
            this.$router.push({ name: "client.master.company" });
          } else {
            this.clearForm();
          }
        })
        .catch(err => { this.errors = err; this.closeBtnLoad(ref); });
    },
  },
};
</script>

<style lang="scss" scoped>
$font:   'Segoe UI', sans-serif;
$blue:   #389ad4;
$dark:   #0d1b3e;
$green:  #276749;
$border: #dde2ec;

.page-wrap {
  padding: 12px 12px 96px;
  font-family: $font;
}

/* ── panel card ── */
.panel-card {
  background: #fff;
  border: 1px solid $border;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0,0,0,0.05);
}

/* ── header ── */
.panel-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 24px;
  border-bottom: 1px solid $border;
  background: linear-gradient(135deg, #f8faff 0%, #f0f5ff 100%);

  &__icon {
    width: 48px; height: 48px; border-radius: 12px;
    background: linear-gradient(135deg, $blue, #2277aa);
    display: flex; align-items: center; justify-content: center;
    color: #fff; font-size: 18px; flex-shrink: 0;
  }
  &__text { flex: 1; }
  &__title { font-size: 16px; font-weight: 700; color: $dark; line-height: 1.2; }
  &__sub   { font-size: 12px; color: #7a8499; margin-top: 3px; }
}

/* ── form body ── */
.form-body {
  padding: 8px 0;
}

/* ── section ── */
.form-section {
  padding: 20px 28px 24px;

  & + & {
    border-top: 1px solid $border;
  }
}

.section-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 700;
  color: $dark;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 18px;

  &__icon { font-size: 13px; color: $blue; }
}

/* ── toggle row ── */
.toggle-row {
  display: flex;
  gap: 16px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.toggle-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  background: #f8faff;
  border: 1.5px solid $border;
  border-radius: 12px;
  padding: 12px 16px;
  flex: 1;
  min-width: 220px;

  &__label { font-size: 13px; font-weight: 600; color: $dark; }
  &__sub   { font-size: 11px; color: #7a8499; margin-top: 2px; }
}

/* CSS toggle switch */
.toggle-switch {
  position: relative;
  display: inline-block;
  cursor: pointer;
  flex-shrink: 0;

  input { display: none; }

  .toggle-track {
    display: block;
    width: 40px; height: 22px;
    background: #d0d7e3;
    border-radius: 11px;
    transition: background 0.2s;
    position: relative;
  }

  .toggle-thumb {
    position: absolute;
    top: 3px; left: 3px;
    width: 16px; height: 16px;
    background: #fff;
    border-radius: 50%;
    box-shadow: 0 1px 4px rgba(0,0,0,0.18);
    transition: transform 0.2s;
  }

  input:checked ~ .toggle-track { background: $blue; }
  input:checked ~ .toggle-track .toggle-thumb { transform: translateX(18px); }
}

/* ── field grid ── */
.field-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px 20px;

  &--single { grid-template-columns: 1fr; max-width: 50%; }
}

.field-group {
  display: flex;
  flex-direction: column;
  gap: 5px;

  &--error .field-input { border-color: #b42318; }
}

.field-label {
  font-size: 12px;
  font-weight: 600;
  color: #5a6278;
  .req { color: #b42318; }
}

.field-input {
  height: 38px;
  border: 1.5px solid $border;
  border-radius: 8px;
  padding: 0 12px;
  font-size: 13px;
  color: $dark;
  font-family: $font;
  outline: none;
  background: #fff;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus {
    border-color: $blue;
    box-shadow: 0 0 0 3px rgba(56,154,212,0.1);
  }
  &::placeholder { color: #b0bac9; }
}

.field-error {
  font-size: 11px;
  color: #b42318;
}

/* ── floating save bar ── */
.save-bar {
  position: fixed;
  bottom: 2.5rem;
  width: calc(78vw);
  left: 50%;
  transform: translateX(-40%);
  background: #1a2340;
  border-radius: 14px;
  padding: 12px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 8px 32px rgba(13,27,62,0.3);
  z-index: 100;

  &__right {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  &__back {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    background: rgba(255,255,255,0.08);
    color: rgba(255,255,255,0.75);
    border: 1px solid rgba(255,255,255,0.15);
    border-radius: 9px;
    padding: 8px 16px;
    font-size: 13px;
    font-weight: 600;
    font-family: $font;
    cursor: pointer;
    transition: background 0.15s;
    &:hover { background: rgba(255,255,255,0.14); }
  }

  &__secondary {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    background: rgba(255,255,255,0.08);
    color: rgba(255,255,255,0.8);
    border: 1px solid rgba(255,255,255,0.2);
    border-radius: 9px;
    padding: 8px 18px;
    font-size: 13px;
    font-weight: 600;
    font-family: $font;
    cursor: pointer;
    transition: background 0.15s;
    &:hover { background: rgba(255,255,255,0.14); }
    &:disabled { opacity: 0.5; cursor: default; }
  }

  &__primary {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    background: $blue;
    color: #fff;
    border: none;
    border-radius: 9px;
    padding: 8px 22px;
    font-size: 13px;
    font-weight: 600;
    font-family: $font;
    cursor: pointer;
    transition: background 0.15s, box-shadow 0.15s;
    &:hover { background: #2d85bb; box-shadow: 0 4px 14px rgba(56,154,212,0.4); }
    &:disabled { opacity: 0.6; cursor: default; box-shadow: none; }
  }
}
</style>
